import { useSyncExternalStore } from 'react'

// The same literals and predicate are spelled in apps/web/index.html because
// the pre-paint script cannot import this module, so a change here must be
// made there too.
export const CONSTRAINED_ENTRY_LOAD_MS = 4000
export const CONSTRAINED_EFFECTIVE_TYPES = ['slow-2g', '2g', '3g'] as const

let entryBundleExecutionMs: number | undefined

export function recordEntryBundleExecuted(): void {
  if (
    entryBundleExecutionMs !== undefined ||
    typeof performance === 'undefined'
  ) {
    return
  }
  entryBundleExecutionMs = performance.now()
}

function readConnection(): unknown {
  if (typeof navigator === 'undefined') return undefined
  return Reflect.get(navigator, 'connection')
}

function connectionSnapshot(connection: unknown): boolean | null {
  if (connection === undefined || connection === null) return null
  if (typeof connection !== 'object') return false

  if (Reflect.get(connection, 'saveData') === true) return true

  const effectiveType = Reflect.get(connection, 'effectiveType')
  return CONSTRAINED_EFFECTIVE_TYPES.some((type) => type === effectiveType)
}

/**
 * Whether this page load should avoid optional, bandwidth-heavy assets.
 * Offline is deliberately not part of this signal; TanStack owns that state.
 */
export function getConstrainedConnectionSnapshot(): boolean {
  const connection = connectionSnapshot(readConnection())
  if (connection !== null) return connection
  return (
    entryBundleExecutionMs !== undefined &&
    entryBundleExecutionMs >= CONSTRAINED_ENTRY_LOAD_MS
  )
}

function subscribe(onChange: () => void): () => void {
  const connection = readConnection()
  if (!(connection instanceof EventTarget)) return () => {}

  connection.addEventListener('change', onChange)
  return () => connection.removeEventListener('change', onChange)
}

export function useConstrainedConnection(): boolean {
  return useSyncExternalStore(
    subscribe,
    getConstrainedConnectionSnapshot,
    () => false,
  )
}
